/**
 * 标准配合求解几何变换工具。
 *
 * 文件职责：
 * 1. 将 Mate Definition 中的世界空间几何描述符按组件累计 delta 变换到当前求解步；
 * 2. 维护“初始选择几何 -> 当前组件位姿”的纯数据映射；
 * 3. 保持求解器内部连续多配合计算不依赖 Three.js 场景对象。
 */

import * as THREE from 'three';
import type { ComponentTransformSnapshot } from '../../component-instance';
import type { AssemblyMateSolverComponentState } from '../StandardMateSolverTypes';
import type { AssemblyMateDefinition, MateEntityRef } from '../../definition';

/**
 * 组件世界空间累计 delta 映射。
 */
export type ComponentWorldDeltaMap = Record<string, THREE.Matrix4>;

/** 需要按点坐标变换的描述符字段。 */
const POINT_FIELD_KEYS = ['point', 'start', 'end', 'center', 'axisOrigin', 'origin', 'apex'] as const;

/** 需要按方向向量变换的描述符字段。 */
const DIRECTION_FIELD_KEYS = ['normal', 'direction', 'axisDirection', 'xAxis', 'yAxis', 'zAxis'] as const;

/**
 * 将三元组转换为 Vector3。
 *
 * @param value 三元组。
 * @returns Vector3。
 */
function vectorFromTuple(value: TopologyPoint3D): THREE.Vector3 {
  return new THREE.Vector3(value[0], value[1], value[2]);
}

/**
 * 将 Vector3 转换为三元组。
 *
 * @param value Vector3。
 * @returns 三元组。
 */
function tupleFromVector(value: THREE.Vector3): TopologyPoint3D {
  return [value.x, value.y, value.z];
}

/**
 * 判断三维点元组。
 *
 * @param value 待判断值。
 * @returns 是否为三维点元组。
 */
function isPointTuple(value: unknown): value is TopologyPoint3D {
  return Array.isArray(value)
    && value.length === 3
    && value.every((item) => typeof item === 'number' && Number.isFinite(item));
}

/**
 * 从 transform 组合矩阵。
 *
 * @param transform transform 快照。
 * @returns 变换矩阵。
 */
function composeTransformMatrix(transform: ComponentTransformSnapshot): THREE.Matrix4 {
  return new THREE.Matrix4().compose(
    vectorFromTuple(transform.position as TopologyPoint3D),
    new THREE.Quaternion(
      transform.quaternion[0],
      transform.quaternion[1],
      transform.quaternion[2],
      transform.quaternion[3],
    ),
    vectorFromTuple(transform.scale as TopologyPoint3D),
  );
}

/**
 * 创建组件累计 delta 映射。
 *
 * @param components 组件状态。
 * @returns 初始 delta 映射。
 */
export function createComponentWorldDeltaMap(
  components: Readonly<Record<string, AssemblyMateSolverComponentState>>,
): ComponentWorldDeltaMap {
  const result: ComponentWorldDeltaMap = {};
  Object.keys(components).forEach((componentId) => {
    result[componentId] = new THREE.Matrix4().identity();
  });
  return result;
}

/**
 * 创建实体从选择瞬间到当前求解步的世界空间 delta。
 *
 * @param entity Mate 实体引用。
 * @param components 当前求解步组件状态。
 * @returns 实体专属 delta；缺少选择位姿或当前组件时返回 `null`。
 */
function createEntitySelectionToCurrentDelta(
  entity: MateEntityRef,
  components: Readonly<Record<string, AssemblyMateSolverComponentState>>,
): THREE.Matrix4 | null {
  const currentComponent = components[entity.componentInstanceId];
  if (!currentComponent) {
    return null;
  }

  /**
   * Mate 几何是选择瞬间的 world space 快照。子装配父节点存在 transform 时，
   * local transform 不能作为 world-space BRep 的基线；因此运行时提供 world transform
   * 时必须优先使用 world 基线。旧文档或纯数据测试缺少 world 基线时，再回退到顶层装配
   * 可用的 local transform。
   */
  const referenceTransform = entity.componentWorldTransformAtSelection
    ?? entity.componentTransformAtSelection
    ?? null;
  const currentTransform = entity.componentWorldTransformAtSelection && currentComponent.worldTransform
    ? currentComponent.worldTransform
    : currentComponent.transform;
  if (!referenceTransform) {
    return null;
  }

  const referenceMatrix = composeTransformMatrix(referenceTransform);
  const currentMatrix = composeTransformMatrix(currentTransform);
  return currentMatrix.multiply(referenceMatrix.invert());
}

/**
 * 记录某组件本次求解产生的世界空间 delta。
 *
 * @param deltaMap 累计 delta 映射。
 * @param componentInstanceId 组件实例 ID。
 * @param previousTransform 求解前 transform。
 * @param nextTransform 求解后 transform。
 */
export function updateComponentWorldDeltaMap(
  deltaMap: ComponentWorldDeltaMap,
  componentInstanceId: string,
  previousTransform: ComponentTransformSnapshot,
  nextTransform: ComponentTransformSnapshot,
): void {
  const previousMatrix = composeTransformMatrix(previousTransform);
  const nextMatrix = composeTransformMatrix(nextTransform);
  const inversePrevious = previousMatrix.clone().invert();
  const currentStepDelta = nextMatrix.multiply(inversePrevious);
  const existingDelta = deltaMap[componentInstanceId] ?? new THREE.Matrix4().identity();

  /**
   * Mate Definition 中的几何始终是选择时世界坐标。
   * 因此累计 delta 必须按“最新步骤在左侧”的方式组合，后续配合才能看到当前几何状态。
   */
  deltaMap[componentInstanceId] = currentStepDelta.multiply(existingDelta);
}

/**
 * 变换点坐标。
 *
 * @param value 原始三元组。
 * @param matrix 变换矩阵。
 * @returns 变换后三元组。
 */
function transformPointTuple(value: TopologyPoint3D, matrix: THREE.Matrix4): TopologyPoint3D {
  return tupleFromVector(vectorFromTuple(value).applyMatrix4(matrix));
}

/**
 * 变换方向向量。
 *
 * @param value 原始三元组。
 * @param normalMatrix 法向矩阵。
 * @returns 变换后三元组。
 */
function transformDirectionTuple(value: TopologyPoint3D, normalMatrix: THREE.Matrix3): TopologyPoint3D {
  const vector = vectorFromTuple(value).applyMatrix3(normalMatrix);
  if (vector.lengthSq() > 0) {
    vector.normalize();
  }
  return tupleFromVector(vector);
}

/**
 * 从 world delta 矩阵中读取半径缩放因子。
 *
 * 设计说明：
 * 圆柱、圆边和球的 radius 是求解器距离/相切判断的重要输入。组件 transform 发生缩放时，点坐标会被
 * 矩阵更新，但半径不会自动变化，因此需要在 descriptor 变换阶段同步缩放。非均匀缩放在 CAD BRep
 * 语义上会把圆变成椭圆；当前装配求解器尚不支持该退化，这里取三轴平均值作为最稳定的近似，并让
 * 后续更严格的几何解析器负责输出 unsupported。
 *
 * @param matrix world delta 矩阵。
 * @returns 半径缩放因子。
 */
function resolveRadiusScaleFactor(matrix: THREE.Matrix4): number {
  const elements = matrix.elements;
  const xScale = Math.hypot(elements[0], elements[1], elements[2]);
  const yScale = Math.hypot(elements[4], elements[5], elements[6]);
  const zScale = Math.hypot(elements[8], elements[9], elements[10]);
  return (xScale + yScale + zScale) / 3;
}

/**
 * 按世界空间 delta 变换 BRep 描述符。
 *
 * @param descriptor 原始描述符。
 * @param matrix 世界空间 delta。
 * @returns 变换后的描述符。
 */
export function transformBRepGeometryDescriptor(
  descriptor: BRepGeometryDescriptor,
  matrix: THREE.Matrix4,
): BRepGeometryDescriptor {
  const normalMatrix = new THREE.Matrix3().getNormalMatrix(matrix);
  const radiusScaleFactor = resolveRadiusScaleFactor(matrix);
  const transformed = { ...descriptor } as BRepGeometryDescriptor & Record<string, unknown>;

  POINT_FIELD_KEYS.forEach((key) => {
    const value = transformed[key];
    if (isPointTuple(value)) {
      transformed[key] = transformPointTuple(value, matrix);
    }
  });

  DIRECTION_FIELD_KEYS.forEach((key) => {
    const value = transformed[key];
    if (isPointTuple(value)) {
      transformed[key] = transformDirectionTuple(value, normalMatrix);
    }
  });

  if (Array.isArray(transformed.sampledPoints)) {
    transformed.sampledPoints = transformed.sampledPoints
      .filter(isPointTuple)
      .map((point) => transformPointTuple(point, matrix));
  }

  if (typeof transformed.radius === 'number' && Number.isFinite(transformed.radius)) {
    transformed.radius *= radiusScaleFactor;
  }

  return transformed;
}

/**
 * 按组件累计 delta 变换 Mate 实体引用。
 *
 * @param entity 原始实体引用。
 * @param deltaMap 组件累计 delta 映射。
 * @returns 当前求解步实体引用。
 */
function transformMateEntityRef(
  entity: MateEntityRef,
  deltaMap: ComponentWorldDeltaMap,
  components: Readonly<Record<string, AssemblyMateSolverComponentState>>,
): MateEntityRef {
  const delta = createEntitySelectionToCurrentDelta(entity, components)
    ?? deltaMap[entity.componentInstanceId]
    ?? null;
  if (!delta) {
    return entity;
  }
  return {
    ...entity,
    geometry: transformBRepGeometryDescriptor(entity.geometry, delta),
  };
}

/**
 * 将 Mate Definition 中的实体几何变换到当前求解步。
 *
 * @param mate 原始 Mate Definition。
 * @param deltaMap 组件累计 delta 映射。
 * @returns 当前求解步 Mate Definition。
 */
export function transformMateDefinitionForSolve(
  mate: AssemblyMateDefinition,
  deltaMap: ComponentWorldDeltaMap,
  components: Readonly<Record<string, AssemblyMateSolverComponentState>>,
): AssemblyMateDefinition {
  return {
    ...mate,
    firstEntity: transformMateEntityRef(mate.firstEntity, deltaMap, components),
    secondEntity: transformMateEntityRef(mate.secondEntity, deltaMap, components),
    ...(mate.referenceEntity
      ? { referenceEntity: transformMateEntityRef(mate.referenceEntity, deltaMap, components) }
      : {}),
  };
}
